import { useState } from "react";
import { Check, ArrowLeft, ArrowRight, Camera } from "lucide-react";
import { COUNTRIES, VISAS } from "./flowData";

const Step = ({ n, label, on }) => (
  <div className="flex items-center gap-2">
    <span className={`h-6 w-6 text-xs flex items-center justify-center font-semibold ${on ? "bg-[#181E4B] text-white" : "border border-[#E7E5DF] text-[#5E6282]"}`} style={{ borderRadius: 4 }}>{n}</span>
    <span className={`text-sm hidden sm:inline ${on ? "text-[#181E4B] font-medium" : "text-[#5E6282]"}`}>{label}</span>
  </div>
);

const Row = ({ active, onClick, title, sub, tag, testid, disabled }) => (
  <button data-testid={testid} disabled={disabled} onClick={onClick}
    className={`w-full text-left px-5 py-4 flex items-center justify-between transition-colors rounded-md ${active ? "border-2 border-[#181E4B] bg-[#FFF1DA]/60" : "border border-[#E7E5DF] bg-white hover:border-[#181E4B]"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
    <span>
      <span className="block font-semibold text-[#1E1D4C]">{title}</span>
      <span className="block text-sm text-[#5E6282] mt-0.5">{sub}{tag ? ` · ${tag}` : ""}</span>
    </span>
    {active && <span className="h-6 w-6 bg-[#F1A501] flex items-center justify-center rounded-sm"><Check size={14} className="text-[#14183E]" /></span>}
  </button>
);

export default function FlowWizard({ onStart }) {
  const [step, setStep] = useState(0);
  const [country, setCountry] = useState("de");
  const [visa, setVisa] = useState("tourist");
  const [scanned, setScanned] = useState(false);
  const titles = [["Where are you travelling?", "Choose the destination country."], ["Which visa do you need?", "Choose the category that matches your trip."], ["Your passport", "We only need the photo page."], ["Confirm your application", "Check these details before we open your guided chat."]];
  const cName = COUNTRIES.find((c) => c.id === country).name;
  const vName = VISAS.find((v) => v.id === visa);

  return (
    <div className="min-h-screen bg-[#FBFAF7]" data-testid="wizard-root">
      <header className="h-16 bg-white border-b border-[#E7E5DF] px-6 flex items-center justify-between">
        <span className="font-serif font-bold text-xl text-[#181E4B]">HolaVisto</span>
        <div className="flex gap-6">
          {["Destination", "Visa type", "Passport", "Confirm"].map((l, i) => <Step key={l} n={i + 1} label={l} on={i <= step} />)}
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-12 sm:py-16">
        <h1 className="font-serif font-bold text-3xl text-[#181E4B]" data-testid="wizard-title">{titles[step][0]}</h1>
        <p className="text-[#5E6282] mt-2 mb-8">{titles[step][1]}</p>

        {step === 0 && <div className="grid sm:grid-cols-2 gap-3" data-testid="wizard-step-destination">
          {COUNTRIES.map((c) => <Row key={c.id} testid={`country-card-${c.id}`} title={c.name} sub={c.sub} active={country === c.id} disabled={!c.live} onClick={() => setCountry(c.id)} />)}
        </div>}

        {step === 1 && <div className="space-y-3" data-testid="wizard-step-visa-type">
          {VISAS.map((v) => <Row key={v.id} testid={`visa-option-${v.id}`} title={v.label} sub={v.sub} tag={v.tag} active={visa === v.id} onClick={() => setVisa(v.id)} />)}
        </div>}

        {step === 2 && <div className="space-y-4" data-testid="wizard-step-passport">
          <label className="block text-sm font-medium text-[#1E1D4C]">Passport issued by</label>
          <select className="w-full border border-[#E7E5DF] bg-white px-4 py-3 rounded-md" defaultValue="Pakistan" data-testid="passport-country-select"><option>Pakistan</option><option>India</option><option>Bangladesh</option></select>
          <button data-testid="passport-scan-btn" onClick={() => setScanned(true)} className="w-full border border-dashed border-[#181E4B] bg-white px-5 py-8 flex flex-col items-center gap-2 rounded-md hover:bg-[#FFF1DA]/40">
            <Camera className="text-[#181E4B]" />
            <span className="font-semibold text-[#1E1D4C]">{scanned ? "Passport read: review the values in the chat" : "Scan passport photo page"}</span>
            <span className="text-sm text-[#5E6282]">Upload a photo or PDF. Optional.</span>
          </button>
          <button data-testid="passport-manual-btn" className="text-sm text-[#181E4B] underline">Prefer typing? Enter the details by hand</button>
        </div>}

        {step === 3 && <div data-testid="wizard-step-confirm">
          <dl className="bg-white border border-[#E7E5DF] rounded-md divide-y divide-[#E7E5DF]">
            {[["Destination", cName], ["Visa type", `${vName.label} (${vName.tag})`], ["Passport", "Pakistan"], ["Deciding mission", "German Embassy, Islamabad (or Karachi, by province)"]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 px-5 py-4"><dt className="text-sm text-[#5E6282]">{k}</dt><dd className="text-sm font-semibold text-[#1E1D4C] text-right">{v}</dd></div>
            ))}
          </dl>
        </div>}

        <div className="flex justify-between mt-10">
          <button data-testid="wizard-back-btn" onClick={() => setStep(Math.max(0, step - 1))} className={`px-4 py-2.5 border border-[#E7E5DF] bg-white text-sm rounded-md flex items-center gap-2 ${step === 0 ? "invisible" : ""}`}><ArrowLeft size={16} />Back</button>
          {step < 3
            ? <button data-testid="wizard-next-btn" onClick={() => setStep(step + 1)} className="px-6 py-2.5 bg-[#181E4B] text-white text-sm font-medium rounded-md flex items-center gap-2 hover:bg-[#14183E]">Continue<ArrowRight size={16} /></button>
            : <button data-testid="wizard-start-app-btn" onClick={onStart} className="px-6 py-2.5 bg-[#F1A501] text-[#14183E] text-sm font-semibold rounded-md hover:bg-[#D99300]">Start my application</button>}
        </div>
      </main>
    </div>
  );
}
